import { FormikProps, FormikValues } from 'formik';
import React from 'react';
import { Button as ReactButton } from '@a-ui/react';
import { translate } from '../../i18n';
import { ITranslations } from '../../i18n/config';
import { useAuthentication } from '../../state/Authentication';
import Box from '../Box';
import CodeField from '../CodeField/CodeField';
import ConfirmationModal from '../ConfirmationModal';
import { DetailLayout } from '../DetailLayout';
import { FormErrors } from '../Form';
import GlobalStateMessage from '../GlobalStateMessage';
import MultiStepForm, { FormStep } from '../MultiStepForm';
import TelephoneField from '../TelephoneField';
import Typography from '../Typography';
import { initialValues, validationSchema } from './config';
import { usePhoneForm } from './hooks';
import PhoneFormInfoBox from './PhoneFormInfobox';
import Link from '../Link';
import useConfig from '../../hooks/config.hooks';

interface PhoneFormProps {
  copyIdentifier: 'add' | 'edit';
  redirectUrl: string;
}

const PhoneForm: React.FC<PhoneFormProps> = ({ copyIdentifier, redirectUrl }) => {
  const form = React.useRef<FormikProps<FormikValues>>(null);
  const [isModalVisible, setModalVisible] = React.useState(false);
  const {
    state: { user },
  } = useAuthentication();
  const { CHANGE_EMAIL_URL } = useConfig();
  const { handleSubmit, sendCode, resendCode, stopProgress } = usePhoneForm(
    form,
    user,
    copyIdentifier,
    redirectUrl,
  );

  const toggleModal = () => {
    setModalVisible(!isModalVisible);
  };

  return (
    <DetailLayout
      backAction={toggleModal}
      backLabel="accessKeys.overview.title"
      title={`accessKeys.phone.${copyIdentifier}.title` as ITranslations}
      description={`accessKeys.phone.${copyIdentifier}.intro` as ITranslations}
    >
      <Box>
        <MultiStepForm
          initialValues={initialValues}
          ref={form}
          onSubmit={handleSubmit}
          initialStep={user.phone?.candidate ? 1 : 0}
        >
          <FormStep validationSchema={validationSchema[0]} onSubmit={sendCode}>
            <Typography variant="h2" as="h6" classNames="u-margin-bottom-xs">
              {translate(`accessKeys.phone.${copyIdentifier}.step1.title` as ITranslations)}
            </Typography>

            <Typography classNames="u-margin-bottom">
              {translate(`accessKeys.phone.form.step1.intro` as ITranslations)}
            </Typography>

            <FormErrors />

            <TelephoneField
              id="phoneNumber"
              name="phoneNumber"
              label={translate('input.label.phone')}
            />

            <ul className="m-button-list u-margin-top">
              <li>
                <ReactButton fullWidth htmlType="submit">
                  {translate('accessKeys.phone.form.step1.confirm')}
                </ReactButton>
              </li>
              <li>
                <ReactButton emphasis="medium" fullWidth htmlType="button" onClick={toggleModal}>
                  {translate('common.button.cancel')}
                </ReactButton>
              </li>
            </ul>
          </FormStep>
          <FormStep validationSchema={validationSchema[1]}>
            <Typography variant="h2" as="h6" classNames="u-margin-bottom-xs">
              {translate('accessKeys.phone.form.step2.title')}
            </Typography>

            <PhoneFormInfoBox form={form} user={user} />

            <Typography classNames="u-margin-top u-text-bold">
              {translate('accessKeys.phone.form.step2.body1_title')}
            </Typography>
            <Typography classNames="u-margin-bottom">
              {translate('accessKeys.phone.form.step2.body1')}
            </Typography>

            <Typography classNames="u-text-bold">
              {translate('accessKeys.phone.form.step2.body2_title')}
            </Typography>
            <Typography classNames={['u-margin-bottom']}>
              {translate('accessKeys.phone.form.step2.body2.before')}
              <Link to={CHANGE_EMAIL_URL} target="_blank">
                {translate('accessKeys.phone.form.step2.body2.link')}
              </Link>
            </Typography>

            <GlobalStateMessage location="accessKeysPhone" />
            <FormErrors />
            <div className="u-margin-top-s">
              <CodeField
                id="code"
                name="code"
                className="u-margin-bottom-xs"
                label={translate('accessKeys.phone.form.step2.label')}
              />
            </div>

            <Typography classNames="u-margin-bottom-xs">
              <small>{translate('accessKeys.phone.form.step2.resend_label')}</small>
              <br />
              <button type="button" className="a-link" onClick={resendCode}>
                <small>{translate('accessKeys.phone.form.step2.resend_link')}</small>
              </button>
            </Typography>

            <ul className="m-button-list u-margin-top">
              <li>
                <ReactButton fullWidth htmlType="submit">
                  {translate('accessKeys.phone.form.step2.confirm')}
                </ReactButton>
              </li>
              <li>
                <ReactButton emphasis="medium" fullWidth htmlType="button" onClick={toggleModal}>
                  {translate('common.button.cancel')}
                </ReactButton>
              </li>
            </ul>
          </FormStep>
        </MultiStepForm>
      </Box>

      <ConfirmationModal
        title={`accessKeys.phone.${copyIdentifier}.modal.title` as ITranslations}
        body={`accessKeys.phone.${copyIdentifier}.modal.description` as ITranslations}
        confirmLabel="common.modal.confirm"
        cancelLabel="common.modal.cancel"
        onRequestClose={toggleModal}
        onConfirm={stopProgress}
        isOpen={isModalVisible}
      />
    </DetailLayout>
  );
};

export default PhoneForm;
